import { useMutation as useApolloMutation } from "@apollo/client";
import { Typography } from "@mui/material";
import { useState } from "react";
import { toast } from "react-toastify";
import {
  CREATE_PUBLIC_TRANSPORT_PLAN,
  CreatePublicTransportPlanRequestInput,
  UPDATE_PUBLIC_TRANSPORT_PLAN,
  UpdatePublicTransportPlanRequestInput
} from "../../../graphql/operations";
import { useModulePlan } from "../../../hooks/useModulePlan";
import { Plan } from "../../../models";
import { EmployerModuleType } from "../../../models/accounts/employerModuleType";
import { isLoading } from "../../../utils/isLoading";
import { useData } from "../../../utils/useData";
import { LoadingSpinner } from "../../shared/LoadingSpinner";
import { ModulePlanFormBase } from "../ModulePlanFormBase";
import { PublicTransportSettingsFields } from "./PublicTransportSettingsFields";

export const PublicTransportForm = ({
  plan,
  onClose
}: {
  plan?: Plan;
  onClose: () => void;
}) => {
  const { plans, serviceSectors, employees, employer, accounts } = useData();
  const { initialValues, validationSchema } = useModulePlan(
    EmployerModuleType.PUBLIC_TRANSPORT,
    plan
  );

  const [error, setError] = useState<string | null>(null);

  const [createPublicTransportPlan, { loading: createPublicTransportLoading }] =
    useApolloMutation<boolean, CreatePublicTransportPlanRequestInput>(
      CREATE_PUBLIC_TRANSPORT_PLAN,
      {
        onCompleted: () => {
          toast.success("Created successfully");
          plans.refetch();
          onClose();
        },
        onError: (error) => {
          console.error(error.message);
          setError("An unexpected error occurred, please contact support.");
        }
      }
    );

  const [updatePublicTransportPlan, { loading: updatePublicTransportLoading }] =
    useApolloMutation<boolean, UpdatePublicTransportPlanRequestInput>(
      UPDATE_PUBLIC_TRANSPORT_PLAN,
      {
        onCompleted: () => {
          toast.success("Updated successfully");
          plans.refetch();
          onClose();
        },
        onError: (error) => {
          console.error(error.message);
          setError("An unexpected error occurred, please contact support.");
        }
      }
    );

  const handlePublicTransportSave = async (values: any) => {
    if (!plan) {
      await createPublicTransportPlan({
        variables: {
          employerId: employer.data!.id,
          request: {
            name: values.name
          }
        }
      });
    } else {
      await updatePublicTransportPlan({
        variables: {
          id: plan.id,
          employerId: employer.data!.id,
          request: {
            name: values.name
          }
        }
      });
    }
  };

  if (isLoading([plans, employees, accounts, serviceSectors]))
    return <LoadingSpinner />;

  return (
    <ModulePlanFormBase
      initialValues={initialValues}
      validationSchema={validationSchema}
      isLoading={
        isLoading([plans, employees, accounts, serviceSectors]) ||
        createPublicTransportLoading ||
        updatePublicTransportLoading
      }
      onClose={onClose}
      onSave={handlePublicTransportSave}
    >
      <PublicTransportSettingsFields />
      {error && <Typography color="error">{error}</Typography>}
    </ModulePlanFormBase>
  );
};
